import { PermissionSetDomain, PermissionSetRules, RecordScope } from '../../../types';

const NONE = '—';

export type AccessLevelKey = 'none' | 'read_only' | 'full_access' | 'mixed';
export type ScopeTypeKey = 'none' | RecordScope | 'mixed';

const ACCESS_LEVEL_LABELS: Record<AccessLevelKey, string> = {
  none: NONE,
  read_only: 'Read Only',
  full_access: 'Full Access',
  mixed: 'Mixed'
};

const SCOPE_TYPE_LABELS: Record<ScopeTypeKey, string> = {
  none: NONE,
  all: 'All Account Data',
  tag: 'Tag-Based',
  mixed: 'Mixed'
};

const enabledDomains = (rules: PermissionSetRules): PermissionSetDomain[] =>
  (Object.keys(rules) as PermissionSetDomain[]).filter((domain) => rules[domain]?.visible !== false);

export const deriveAccessLevelKey = (rules: PermissionSetRules): AccessLevelKey => {
  const enabled = enabledDomains(rules);
  if (enabled.length === 0) return 'none';
  if (enabled.every((domain) => rules[domain]?.write === false)) return 'read_only';
  if (enabled.every((domain) => rules[domain]?.write !== false)) return 'full_access';
  return 'mixed';
};

export const deriveScopeTypeKey = (rules: PermissionSetRules): ScopeTypeKey => {
  const enabled = enabledDomains(rules);
  if (enabled.length === 0) return 'none';
  const scopes = new Set<RecordScope>(enabled.map((domain) => rules[domain]?.record_scope ?? 'all'));
  if (scopes.size > 1) return 'mixed';
  return [...scopes][0];
};

export const deriveAccessLevel = (rules: PermissionSetRules): string =>
  ACCESS_LEVEL_LABELS[deriveAccessLevelKey(rules)];

export const deriveScopeType = (rules: PermissionSetRules): string => SCOPE_TYPE_LABELS[deriveScopeTypeKey(rules)];

export const deriveDataFilter = (rules: PermissionSetRules, tagNames?: Record<string, string>): string => {
  const enabled = enabledDomains(rules);
  const tagIds = uniqueTagIds(rules, enabled);
  const hiddenCount = enabled.reduce((sum, domain) => sum + (rules[domain]?.hide_fields?.length ?? 0), 0);

  const parts: string[] = [];
  if (tagIds.length) parts.push(tagSummary(tagIds, tagNames));
  if (hiddenCount) parts.push(`${hiddenCount} ${hiddenCount === 1 ? 'field' : 'fields'} hidden`);
  return parts.length ? parts.join(' · ') : 'All data';
};

const uniqueTagIds = (rules: PermissionSetRules, enabled: PermissionSetDomain[]): string[] => {
  const ids = enabled
    .filter((domain) => rules[domain]?.record_scope === 'tag')
    .flatMap((domain) => rules[domain]?.record_scope_tags ?? []);
  return [...new Set(ids)];
};

const tagSummary = (tagIds: string[], tagNames?: Record<string, string>): string => {
  if (tagNames) return `Tags: ${tagIds.map((id) => tagNames[id] ?? id).join(', ')}`;
  return `${tagIds.length} ${tagIds.length === 1 ? 'tag' : 'tags'}`;
};
